import { z } from 'zod';
import { type ConnectionDeclarer, connectionAuthSchema, connectionTestSchema } from './connections';
import { serviceIdSchema } from './plugin-requirements';
import { pluginSettingsSchema } from './settings';
import { widgetDeclarationSchema } from './widgets';
import { acpAgentContributionSchema } from './workbench';

/**
 * `crowdex.json`, at a plugin's root: what the plugin is (a provider of core contracts,
 * an app with views, or both), what it may do, and what the core renders for it.
 */
export const CROWDEX_MANIFEST_FILE = 'crowdex.json';

const nameSchema = z
  .string()
  .min(1)
  .max(96)
  .regex(/^[a-z0-9][a-z0-9._-]*$/);
const idSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-zA-Z0-9][a-zA-Z0-9._-]*$/);
const titleSchema = z.string().trim().min(1).max(120);
const iconSchema = z.string().min(1).max(512);
/**
 * The app's icon (`display.icon`): a template image, as macOS, Microsoft Teams (`outline`) or
 * VS Code (activity bar) ask of the icons of their sidebars. A monochrome SVG of the package on a
 * transparent background, of at most 64 KB, whose shape only counts: the core paints it with the
 * theme's ink wherever it shows the app (All apps, favourites, tabs, launcher, Plugins page), as it does its
 * own glyphs. An app with a page needs one.
 */
export const appIconSchema = z
  .string()
  .max(256)
  .refine(
    (path) =>
      /^\.\/[^\\]+\.svg$/.test(path) &&
      path
        .slice(2)
        .split('/')
        .every((part) => part !== '' && part !== '.' && part !== '..'),
    'The icon is a ./path to an .svg file of the package.',
  );
/** Where a build keeps the app's icon (`display.icon`), next to `crowdex.json`. */
export const APP_ICON_FILE = 'icon.svg';

/** A route of the app: an absolute URL path with an optional query, never leaving the origin. */
export const appRouteSchema = z
  .string()
  .max(512)
  .regex(
    /^\/[A-Za-z0-9\-._~!$&'()*+,;=:@%/]*(?:\?[A-Za-z0-9\-._~!$&'()*+,;=:@%/?]*)?$/,
    'Routes must be absolute URL paths.',
  )
  .refine(
    (path) => !path.split('?')[0]?.split('/').includes('..'),
    'Routes must not contain "..".',
  );

const moduleEntrySchema = z
  .string()
  .regex(
    /^\.\/[a-zA-Z0-9_./-]+\.(?:tsx?|jsx?|mjs)$/,
    'Entries must be relative JavaScript or TypeScript modules.',
  )
  .refine((path) => !path.split('/').includes('..'), 'Entries must stay inside the plugin.');

/** `https://host[:port]`, or `http://` for loopback during development. */
const originSchema = z
  .string()
  .max(256)
  .refine((value) => {
    try {
      const url = new URL(value);
      const loopback = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
      return (
        url.origin === value &&
        (url.protocol === 'https:' || (url.protocol === 'http:' && loopback))
      );
    } catch {
      return false;
    }
  }, 'Origins are https://host[:port] without a path.');

export const CROWDEX_CAPABILITIES = [
  'files',
  'storage',
  'triggers',
  'connections',
  'agents',
  'tools',
  'events',
  'voice',
  'inference',
  'browser',
  'commands',
  'widgets',
  'tasks',
  'requests',
] as const;
export const crowdexCapabilitySchema = z.enum(CROWDEX_CAPABILITIES);
export type CrowdexCapability = z.infer<typeof crowdexCapabilitySchema>;

/**
 * The file spaces (`docs/adr-file-space-providers.md`): `workspace:` the conversation's, `drive:` the
 * account's, served by its files provider (`files.v1`), `plugin:` the plugin's own build.
 */
export const FILE_SPACES = ['workspace', 'drive', 'plugin'] as const;
export type FileSpace = (typeof FILE_SPACES)[number];

/** `<space>:<glob>`, e.g. `workspace:.crowdex/planner/*.md`. */
export const spacePatternSchema = z
  .string()
  .max(256)
  .regex(/^(workspace|drive|plugin):[^\0]+$/, 'Patterns are "<space>:<glob>".')
  .refine((value) => {
    const glob = value.slice(value.indexOf(':') + 1);
    return !glob.startsWith('/') && !glob.split('/').includes('..');
  }, 'Patterns stay inside their space.');

/**
 * One list: capability names, `network:<origin>`, `files:<space>:<glob>`,
 * `agents.unattended`, `media.camera`, `media.microphone`, and `connections.actions` (with
 * `connections`: run catalog actions and triggers with the user's connections of any app).
 */
export const crowdexPermissionSchema = z.union([
  crowdexCapabilitySchema,
  z.enum(['agents.unattended', 'connections.actions', 'media.camera', 'media.microphone']),
  z.templateLiteral(['network:', z.string()]).refine((value) => {
    return originSchema.safeParse(value.slice('network:'.length)).success;
  }, 'network:<origin>'),
  z.templateLiteral(['files:', z.string()]).refine((value) => {
    return spacePatternSchema.safeParse(value.slice('files:'.length)).success;
  }, 'files:<space>:<glob>'),
]);
export type CrowdexPermission = z.infer<typeof crowdexPermissionSchema>;

export const PROVIDED_CONTRACTS = [
  'forge.v1',
  'inference.v1',
  'realtime.v1',
  'rooms.v1',
  'files.v1',
] as const;

/** The wire formats the core speaks to a files provider's API (`files.v1`). */
export const FILES_FORMATS = ['crowdex-files'] as const;
export type FilesFormat = (typeof FILES_FORMATS)[number];

/**
 * A files provider's API (`files.v1`): its format, and its root, a path under the API of the
 * provider's connection, which signs every call.
 */
export const filesApiSchema = z
  .object({
    format: z.enum(FILES_FORMATS),
    root: z
      .string()
      .min(1)
      .max(512)
      .refine(
        (root) =>
          !root.includes('..') && !root.startsWith('/') && !/^[a-z][a-z0-9+.-]*:/i.test(root),
        "A path under the connection's API",
      ),
  })
  .strict();
export type FilesApi = z.infer<typeof filesApiSchema>;

/** The wire formats the core speaks to a model provider's API. */
export const INFERENCE_FORMATS = ['openai', 'elevenlabs', 'mistral'] as const;
export type InferenceFormat = (typeof INFERENCE_FORMATS)[number];
/** Where a format lists its models, from the API's root: the root is the URL without it. */
export const INFERENCE_MODELS_PATH: Record<InferenceFormat, string> = {
  openai: '/models',
  elevenlabs: '/v1/models',
  mistral: '/v1/models',
};

/**
 * A model provider's API (`inference.v1`): its format, and the URL of its models — relative to
 * the API of the provider's connection, which signs every call, or absolute (a local server, an
 * open one), reached as it is.
 */
export const inferenceApiSchema = z
  .object({
    format: z.enum(INFERENCE_FORMATS),
    models: z
      .string()
      .min(1)
      .max(2048)
      .refine(
        (url) =>
          !url.includes('..') && (/^https?:\/\//.test(url) || !/^[a-z][a-z0-9+.-]*:/i.test(url)),
        "A path under the connection's API, or an http(s) URL",
      ),
  })
  .strict()
  .superRefine((api, ctx) => {
    if (
      !new URL(api.models, 'http://relative.invalid/').pathname.endsWith(
        INFERENCE_MODELS_PATH[api.format],
      )
    )
      ctx.addIssue({
        code: 'custom',
        path: ['models'],
        message: `A ${api.format} API lists its models at <root>${INFERENCE_MODELS_PATH[api.format]}`,
      });
  });
export type InferenceApi = z.infer<typeof inferenceApiSchema>;

export const providedContractSchema = z
  .object({
    contract: z.enum(PROVIDED_CONTRACTS),
    id: idSchema,
    title: titleSchema,
    icon: iconSchema.optional(),
    /** The service the provider uses, from `connections`. */
    connection: serviceIdSchema.optional(),
    /** The hosts a forge serves (`github.com`): the core picks the provider by repository. */
    hosts: z
      .array(z.string().regex(/^[a-z0-9.-]{1,253}(?::[0-9]{1,5})?$/))
      .max(16)
      .optional(),
    /**
     * A forge's other servers (GitHub Enterprise, a self-managed GitLab): a connection type of
     * its own whose every connection serves the host its `field` holds (`host` by default).
     */
    servers: z
      .object({
        connection: serviceIdSchema,
        field: z
          .string()
          .regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/)
          .default('host'),
      })
      .strict()
      .optional(),
    /** A model provider's API, which the core calls in its format (`inference.v1` only). */
    api: inferenceApiSchema.optional(),
    /** A files provider's API, which the hub calls in its format (`files.v1` only). */
    files: filesApiSchema.optional(),
  })
  .strict()
  .superRefine((provided, ctx) => {
    if (provided.contract === 'files.v1' && (provided.files === undefined || !provided.connection))
      ctx.addIssue({
        code: 'custom',
        path: ['files'],
        message: 'A files provider declares its API (format and root) and the connection it uses',
      });
    if (provided.contract !== 'forge.v1' && provided.servers !== undefined)
      ctx.addIssue({
        code: 'custom',
        path: ['servers'],
        message: 'Only forge.v1 declares servers',
      });
    if (provided.contract !== 'files.v1' && provided.files !== undefined)
      ctx.addIssue({ code: 'custom', path: ['files'], message: 'Only files.v1 declares files' });
    if (provided.contract === 'inference.v1' && provided.api === undefined)
      ctx.addIssue({
        code: 'custom',
        path: ['api'],
        message: 'A model provider declares its API: its format and the URL of its models',
      });
    if (provided.contract !== 'inference.v1' && provided.api !== undefined)
      ctx.addIssue({ code: 'custom', path: ['api'], message: 'Only inference.v1 declares an API' });
    if (
      provided.api !== undefined &&
      !/^https?:\/\//.test(provided.api.models) &&
      provided.connection === undefined
    )
      ctx.addIssue({
        code: 'custom',
        path: ['api', 'models'],
        message: 'A relative URL is under the API of a connection: name it',
      });
  });
export type ProvidedContract = z.infer<typeof providedContractSchema>;

/**
 * A service the plugin connects to (`<plugin>/<service>`, or another's, such as a catalog service).
 * For its own service it declares the connection type: the ways of connecting (`auth`) and a check.
 */
export const connectionDeclarationSchema = z
  .object({
    service: serviceIdSchema,
    required: z.boolean().default(false),
    purpose: z.string().max(500).optional(),
    /** OAuth scopes it needs on top of those the app's type asks for. */
    scopes: z.array(z.string().min(1).max(256)).max(64).optional(),
    title: z.string().min(1).max(120).optional(),
    description: z.string().max(1024).optional(),
    auth: z.array(connectionAuthSchema).min(1).max(8).optional(),
    test: connectionTestSchema.optional(),
  })
  .strict();
export type AppConnectionDeclaration = z.infer<typeof connectionDeclarationSchema>;

/**
 * `{{field}}`: a value of the server's connection, filled in where it runs, as everywhere a
 * manifest names a connection's values (`connections[].auth`, `api`, `test`). `${field}`, the
 * form manifests used before, is still read the same way.
 */
const CONNECTION_VARIABLE = /\{\{\s*[A-Za-z_][A-Za-z0-9_]*\s*\}\}|\$\{[A-Za-z_][A-Za-z0-9_]*\}/g;

/**
 * A remote MCP server whose tools the agents get. With a `connection`, its URL and headers may
 * name the connection's values (`{{url}}/mcp`, `Bearer {{access_token}}`); a value the
 * connection does not have fails the call, it is never sent empty.
 */
export const remoteMcpServerSchema = z
  .object({
    id: idSchema,
    title: titleSchema.optional(),
    url: z
      .string()
      .max(2048)
      .refine((value) => {
        const filled = value.replace(CONNECTION_VARIABLE, (_match, offset: number) =>
          offset === 0 ? 'https://connection.invalid' : 'value',
        );
        return URL.canParse(filled) && /^https?:$/.test(new URL(filled).protocol);
      }, 'An HTTP(S) URL, which may name its connection values ({{field}}).'),
    headers: z.record(z.string().max(128), z.string().max(4096)).optional(),
    connection: serviceIdSchema.optional(),
  })
  .strict()
  .refine(
    (server) =>
      server.connection !== undefined ||
      ![server.url, ...Object.values(server.headers ?? {})].some((value) =>
        new RegExp(CONNECTION_VARIABLE.source).test(value),
      ),
    'Only a server with a connection names its values.',
  );

const pageBase = { title: titleSchema };

/**
 * A section of the app's navigation, drawn by the core in the sidebar: a segment of its
 * "Conversations | …" toggle while the app's tab is the one shown, whose `route` lists its items
 * (`{ items }`, in the conversation's workspace). An item picked there reaches the view as
 * `context.item`, with `context.section` the section's id. Apps never draw a sidebar of their own.
 */
export const pageSidebarSchema = z
  .object({
    id: idSchema,
    title: titleSchema,
    route: appRouteSchema,
  })
  .strict();
export type PageSidebar = z.infer<typeof pageSidebarSchema>;

/**
 * An example of what to ask, on the app's home: picking it starts a conversation with the app
 * docked and `prompt` in the composer, for the user to complete and send.
 */
export const pageExampleSchema = z
  .object({
    title: titleSchema,
    prompt: z.string().trim().min(1).max(1000),
  })
  .strict();
export type PageExample = z.infer<typeof pageExampleSchema>;

/**
 * The app's page. Outside a conversation the core shows the app's home (its templates, the
 * user's items, "New …"); from a conversation a view of the app (`path`) docks as a tab of the
 * side panel, its navigation in the sidebar (`sidebars`). A remote web app (`url`) shows its page.
 */
export const crowdexPageSchema = z.union([
  z
    .object({
      ...pageBase,
      path: appRouteSchema,
      sidebars: z
        .array(pageSidebarSchema)
        .min(1)
        .max(4)
        .refine(
          (sidebars) => new Set(sidebars.map((sidebar) => sidebar.id)).size === sidebars.length,
          'Each sidebar has its own id.',
        )
        .optional(),
      /** Example prompts its home offers (beside its templates). */
      examples: z.array(pageExampleSchema).max(6).optional(),
    })
    .strict(),
  z
    .object({
      ...pageBase,
      url: z.union([
        z.url().max(2048),
        z
          .object({
            connection: z
              .object({
                service: serviceIdSchema,
                field: idSchema,
                /**
                 * For an OAuth connection: a path of its API the hub POSTs to with the
                 * connection (`{"redirectTo": "/"}`), answering `{"url"}` on the page's origin
                 * that signs the page in once — the web app opens signed in.
                 */
                ticket: z
                  .string()
                  .regex(/^\/[^\s?#]*$/)
                  .max(256)
                  .optional(),
              })
              .strict(),
          })
          .strict(),
      ]),
    })
    .strict(),
]);
export type CrowdexPage = z.infer<typeof crowdexPageSchema>;

/** A media type (`video/mp4`, `image/png`), lower case, without parameters. */
const mediaTypeSchema = z
  .string()
  .max(128)
  .regex(/^[a-z0-9][a-z0-9!#$&^_.+-]*\/[a-z0-9][a-z0-9!#$&^_.+-]*$/, 'A media type, as video/mp4.');

export const artifactTypeSchema = z
  .object({
    type: z
      .string()
      .max(128)
      .regex(/^[a-z0-9][a-z0-9._-]*\/[a-z0-9][a-z0-9._-]*$/, 'Types are "<namespace>/<name>".'),
    title: titleSchema,
    icon: iconSchema.optional(),
    match: spacePatternSchema,
    entry: z.string().max(256).optional(),
    view: appRouteSchema.optional(),
    edit: z.boolean().default(false),
    describe: z.boolean().default(false),
    publish: z.boolean().default(false),
    /**
     * Examples of this type the plugin ships (`plugin:<glob>`, read-only): artifacts tagged
     * `template`, like the ones a user tags. The app's tab lists the templates while nothing is
     * selected; using one duplicates it.
     */
    templates: spacePatternSchema.optional(),
    /**
     * Copies one of its artifacts (`POST { space, path }`) into the open conversation's workspace:
     * `{ path, item }`, the copy and its item in the app's sidebar. How a template is used.
     */
    duplicate: appRouteSchema.optional(),
    /**
     * Its compact preview, for a conversation's preview card (140 px high): a route of the app
     * answering `GET <route>?space=…&path=…` with `{ image }` (a thumbnail), `{ rows }` (first
     * rows of a table) or `{ text }` (`toolRefPreviewSchema`, clipped to fit). An empty answer, a
     * failure, or no route: the card shows the type's view, as before.
     */
    preview: appRouteSchema.optional(),
    /**
     * A guided creation of this type from the app's home: "New …" (`label`, else "New <title>")
     * starts a conversation that runs the plugin's skill `skill` (a folder of its `skills`), which
     * asks what it needs, then makes the artifact and opens it; the home's composer starts one
     * with the user's message. Without it, the home keeps its plain "New …".
     */
    create: z
      .object({
        skill: z
          .string()
          .max(64)
          .regex(/^[a-z0-9][a-z0-9._-]*$/, 'A skill folder of the plugin, as create-automation.'),
        label: titleSchema.optional(),
      })
      .strict()
      .optional(),
    /**
     * What a source of this type can be rendered to (a video project to an MP4, a design to a PNG):
     * each media type and the `actions` id (an artifact action of the type) producing it. A render
     * is an ordinary file, read by the core's common viewers and linked to its source in the
     * artifact index (`derivedFrom`); a command run of the plugin on an artifact of the type writing
     * an output of one of these media types records it.
     */
    outputs: z
      .array(z.object({ mediaType: mediaTypeSchema, action: idSchema }).strict())
      .max(8)
      .refine(
        (outputs) => new Set(outputs.map((output) => output.mediaType)).size === outputs.length,
        'Each output has its own media type.',
      )
      .optional(),
  })
  .strict()
  .refine((type) => type.templates === undefined || type.duplicate !== undefined, {
    message: 'Templates need a duplicate route to be used.',
    path: ['duplicate'],
  });
export type ArtifactType = z.infer<typeof artifactTypeSchema>;
export type ArtifactTypeOutput = NonNullable<ArtifactType['outputs']>[number];

const containerSchema = z.enum(['main', 'side', 'dialog', 'window']);
export const actionRunSchema = z.union([
  z
    .object({
      open: appRouteSchema,
      in: containerSchema.optional(),
      input: z.unknown().optional(),
      size: z.enum(['default', 'wide']).optional(),
    })
    .strict(),
  /** Asks the agent: the text goes to the open conversation, or a new one. */
  z.object({ ask: z.string().min(1).max(16_000) }).strict(),
  z.object({ call: appRouteSchema }).strict(),
]);
export const crowdexActionSchema = z
  .object({
    id: idSchema,
    label: titleSchema,
    icon: iconSchema.optional(),
    /** `card`: on the cards of data widgets (conversation cards, shown widgets); a `call` gets the widget's ref. */
    where: z.enum(['composer', 'artifact', 'card']),
    when: z
      .object({ types: z.array(z.string().max(128)).max(16).optional() })
      .strict()
      .optional(),
    state: appRouteSchema.optional(),
    confirm: z.string().max(500).optional(),
    run: actionRunSchema,
  })
  .strict();
export type CrowdexAction = z.infer<typeof crowdexActionSchema>;

const programSchema = z.union([
  z.object({ npm: z.string().min(1).max(214), bin: z.string().max(128).optional() }).strict(),
  z.object({ binary: z.string().regex(/^[a-zA-Z0-9._-]{1,64}$/) }).strict(),
]);
const commandParamSchema = z.union([
  z
    .object({
      type: z.literal('string'),
      pattern: z.string().max(512),
      maxLength: z.number().int().positive().max(4096).optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('integer'),
      minimum: z.number().int().optional(),
      maximum: z.number().int().optional(),
    })
    .strict(),
  z.object({ type: z.literal('boolean') }).strict(),
  z
    .object({ type: z.literal('enum'), values: z.array(z.string().max(128)).min(1).max(64) })
    .strict(),
  z
    .object({
      artifact: z.string().max(128),
      file: z.string().max(256).optional(),
      /**
       * The run works on the workspace's own artifact, which it may write (an editor saving a
       * project), instead of a copy. The app must be allowed to write where the artifact is.
       */
      edit: z.boolean().optional(),
    })
    .strict(),
  z.object({ output: z.string().max(256), mediaType: z.string().max(128) }).strict(),
]);
export const crowdexCommandSchema = z
  .object({
    id: idSchema,
    title: titleSchema,
    run: z.tuple([programSchema], z.string().max(4096)).refine((argv) => argv.length <= 64),
    params: z.record(idSchema, commandParamSchema).optional(),
    env: z.record(z.string().regex(/^[A-Z_][A-Z0-9_]{0,63}$/), z.string().max(4096)).optional(),
    network: z
      .union([z.literal(false), z.object({ origins: z.array(originSchema).max(32) })])
      .optional(),
    port: z.boolean().default(false),
    /**
     * Paths of the run's origin the app answers instead of the run, on its own route
     * `/_port/<command><path>`: its own build of the program's web UI, beside the run's API. A
     * path ending in `/*` names everything under it.
     */
    appPaths: z
      .array(z.string().regex(/^\/[^\s?#*]{0,255}(?:\/\*)?$/))
      .max(16)
      .default([]),
    /** An artifact parameter whose copy the run starts in (a project the program expects as cwd). */
    cwd: idSchema.optional(),
    longRunning: z.boolean().default(false),
    timeoutSeconds: z.number().int().positive().max(3600).default(300),
    /**
     * Entries of the run directory kept across the app's runs on one execution (a build cache),
     * or caches of its `HOME` (`home/.cache/<name>`: a downloaded browser): each is a link into
     * the app's own state directory.
     */
    persist: z
      .array(
        z
          .string()
          .regex(
            /^(?:home\/\.cache\/)?(?!(?:home|tmp|out|in|node_modules)$)\.?[a-zA-Z0-9][a-zA-Z0-9._-]{0,63}$/,
          ),
      )
      .max(8)
      .default([]),
    /** Programs the core provides on the run's `PATH`: `ffmpeg` (FFmpeg and FFprobe). */
    tools: z
      .array(z.enum(['ffmpeg']))
      .max(1)
      .default([]),
    platforms: z
      .array(z.enum(['darwin', 'linux', 'win32']))
      .min(1)
      .optional(),
  })
  .strict();
export type CrowdexCommand = z.infer<typeof crowdexCommandSchema>;

/** How the user hears of an event of a type, unless they chose otherwise in their settings. */
export const EVENT_DELIVERIES = ['off', 'list', 'toast', 'native'] as const;
export const eventDeliverySchema = z.enum(EVENT_DELIVERIES);
export type EventDelivery = z.infer<typeof eventDeliverySchema>;

/** A field of an event that subscribers filter on: a string, number, boolean or one of values. */
export const eventFieldSchema = z.union([
  z.enum(['string', 'number', 'boolean']),
  z.object({ enum: z.array(z.string().min(1).max(64)).min(1).max(32) }).strict(),
]);
export type EventField = z.infer<typeof eventFieldSchema>;

/**
 * A type of event the plugin publishes (`events.publish`), as `<plugin>/<type>`: what the
 * user is told of by default, and the fields agents subscribing to it filter on.
 */
export const eventTypeSchema = z
  .object({
    type: idSchema,
    title: titleSchema,
    description: z.string().max(500).optional(),
    severity: z.enum(['info', 'success', 'warning', 'error']).default('info'),
    notify: eventDeliverySchema.default('toast'),
    fields: z.record(idSchema, eventFieldSchema).default({}),
    /**
     * Published by this Crowlingo service rather than the plugin's app: its name in crowid
     * (`crowroom`). The service posts it to the cloud (`@crow/service-kit/events`), which publishes
     * it as this plugin's for the people it is for; a plugin declaring it needs no code.
     */
    service: z
      .string()
      .regex(/^[a-z][a-z0-9-]{0,39}$/)
      .optional(),
    /**
     * An event of it means something waits on the user (an approval, a question, a review): its
     * group's latest unread one, about a conversation (`conversation`), puts that conversation in
     * Needs you until it is read or the group moves on. Its optional `kind` field says what
     * (`question` reads as a question, anything else as an approval).
     */
    waits: z.boolean().optional(),
  })
  .strict()
  .refine((type) => Object.keys(type.fields).length <= 16, 'At most 16 fields.');
export type EventType = z.infer<typeof eventTypeSchema>;

/**
 * A VS Code color theme the plugin offers in Settings › Appearance: a `*-color-theme.json` file
 * of its `files` folder (`path` is relative to it), for the light or the dark appearance.
 */
export const crowdexThemeContributionSchema = z
  .object({
    id: idSchema,
    label: titleSchema,
    type: z.enum(['light', 'dark']),
    path: z
      .string()
      .regex(/^[a-zA-Z0-9_][a-zA-Z0-9_./-]*\.jsonc?$/)
      .refine((path) => !path.split('/').includes('..')),
  })
  .strict();
export type CrowdexThemeContribution = z.infer<typeof crowdexThemeContributionSchema>;

/** A template a task provider installs as the user's automations (`tasks.templates`). */
const taskTemplateSchema = z
  .object({
    id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/),
    name: z.string().trim().min(1).max(80),
    /** Its steps in one line ("Develop, review ×3, PR"). */
    summary: z.string().max(300).default(''),
    /** What it installs (a template of the plugin's, `template:<id>`). */
    definition: z.string().min(1).max(512),
    /** The one a new assistant runs its tasks with: one at most across its providers. */
    default: z.boolean().default(false),
  })
  .strict();
export type TaskTemplate = z.infer<typeof taskTemplateSchema>;

/**
 * A task provider (`tasks.v1`, docs/adr-tasks-and-automations.md): the templates it installs as
 * the user's automations, and the routes the core calls. `list` answers `taskAutomationsSchema`
 * (the automations a task may run, the templates installed on its first call); `adopt` takes
 * `taskAdoptInputSchema` and answers `taskAdoptedSchema` (migration only); `start` gets a
 * `taskStartSchema` body and answers `{ run }`; `stop` gets `{ task, run }`. The provider reports
 * with `tasks.report` (the `tasks` permission). `workflows`, the templates' former name, is still
 * read as `templates`.
 */
export const tasksProviderSchema = z
  .object({
    templates: z.array(taskTemplateSchema).max(32).optional(),
    /** @deprecated Read as `templates`. */
    workflows: z.array(taskTemplateSchema).max(32).optional(),
    list: appRouteSchema.optional(),
    adopt: appRouteSchema.optional(),
    start: appRouteSchema,
    stop: appRouteSchema,
  })
  .strict()
  .refine((input) => input.templates === undefined || input.workflows === undefined, {
    message: 'Give `templates`, not `workflows` beside it.',
  })
  .transform(({ templates, workflows, ...routes }) => ({
    templates: templates ?? workflows ?? [],
    ...routes,
  }));

export const crowdexManifestSchema = z
  .object({
    name: nameSchema,
    version: z.string().min(1).max(64),
    display: z
      .object({
        title: titleSchema,
        icon: appIconSchema.optional(),
        description: z.string().max(2000).optional(),
        category: z.string().max(64).optional(),
      })
      .strict(),
    app: z.object({ entry: moduleEntrySchema }).strict().optional(),
    /**
     * The Crowlingo application this plugin brings into Crowdex (`crowdrive`): shown with its
     * origin (a "Crowlingo" badge and dot, the product switcher opens its tab), and its page told
     * it is embedded (it hides its own top bar). docs/adr/0004-service-roles.md, decision 5.
     */
    service: z
      .string()
      .regex(/^[a-z][a-z0-9-]{0,39}$/)
      .optional(),
    connections: z.array(connectionDeclarationSchema).max(32).optional(),
    permissions: z.array(crowdexPermissionSchema).max(128).default([]),
    requires: z
      .object({ plugins: z.array(nameSchema).max(32).optional() })
      .strict()
      .optional(),
    provides: z.array(providedContractSchema).max(16).optional(),
    agents: z.array(acpAgentContributionSchema).max(16).optional(),
    mcp: z.array(remoteMcpServerSchema).max(16).optional(),
    page: crowdexPageSchema.optional(),
    /**
     * During the core's voice calls: a view shown (an avatar), which reads the call's state, never
     * the conversation; and the route of the personas the user talks to (`callPersonasSchema`:
     * each one's name, personality, instructions and voice, and the one selected), read when a
     * call or an assistant turn starts, `POST { select }` when the user switches in a call.
     */
    call: z
      .object({ view: appRouteSchema, personas: appRouteSchema.optional() })
      .strict()
      .optional(),
    types: z.array(artifactTypeSchema).max(32).optional(),
    tasks: tasksProviderSchema.optional(),
    actions: z.array(crowdexActionSchema).max(64).optional(),
    /** Widgets it provides (`widgetDeclarationSchema`): the core renders them anywhere. */
    widgets: z.array(widgetDeclarationSchema).max(32).optional(),
    tools: z.boolean().default(false),
    /**
     * The code-mode namespace of its tools (`slack_threads`), where the last segment of its name
     * would not do: the namespace of a remote MCP server it also declares has it.
     */
    toolsNamespace: z
      .string()
      .regex(
        /^[a-z][a-zA-Z0-9_]{1,39}$/,
        'An identifier: a lowercase letter, then letters, digits, _.',
      )
      .optional(),
    commands: z.array(crowdexCommandSchema).max(16).optional(),
    events: z.array(eventTypeSchema).max(64).optional(),
    skills: z
      .string()
      .regex(/^\.\/[a-zA-Z0-9_./-]+$/)
      .refine((path) => !path.split('/').includes('..'))
      .optional(),
    /** A folder shipped with the build: the plugin's read-only `plugin:` space. */
    files: z
      .string()
      .regex(/^\.\/[a-zA-Z0-9_./-]+$/)
      .refine((path) => !path.split('/').includes('..'))
      .optional(),
    themes: z.array(crowdexThemeContributionSchema).max(32).optional(),
    settings: pluginSettingsSchema.optional(),
  })
  .strict()
  .superRefine((manifest, ctx) => {
    const needsApp =
      (manifest.page !== undefined && 'path' in manifest.page) ||
      manifest.call !== undefined ||
      manifest.types?.some(
        (type) => type.view !== undefined || type.describe || type.duplicate !== undefined,
      ) ||
      manifest.tasks !== undefined ||
      manifest.actions?.some(
        (action) => action.state !== undefined || 'call' in action.run || 'open' in action.run,
      ) ||
      manifest.tools ||
      (manifest.widgets?.length ?? 0) > 0 ||
      // The core calls a model or files provider's API itself; other contracts are an app's routes.
      (manifest.provides ?? []).some(
        (provided) => provided.contract !== 'inference.v1' && provided.contract !== 'files.v1',
      );
    // A guided creation runs one of the plugin's skills.
    for (const [index, type] of (manifest.types ?? []).entries())
      if (type.create !== undefined && manifest.skills === undefined)
        ctx.addIssue({
          code: 'custom',
          path: ['types', index, 'create', 'skill'],
          message: 'A guided creation runs a skill of the plugin: it needs `skills`.',
        });
    if (needsApp && manifest.app === undefined)
      ctx.addIssue({ code: 'custom', path: ['app'], message: 'Routes and tools need an app.' });
    // A page makes it an app of All apps and the favourites, among glyphs: it brings its own.
    if (manifest.page !== undefined && manifest.display.icon === undefined)
      ctx.addIssue({
        code: 'custom',
        path: ['display', 'icon'],
        message: 'An app with a page needs an icon for All apps.',
      });
    const unique = (path: string, ids: readonly string[] | undefined) => {
      const seen = new Set<string>();
      for (const id of ids ?? []) {
        if (seen.has(id))
          ctx.addIssue({ code: 'custom', path: [path], message: `Duplicate id ${id}` });
        seen.add(id);
      }
    };
    unique(
      'actions',
      manifest.actions?.map((action) => action.id),
    );
    unique(
      'types',
      manifest.types?.map((type) => type.type),
    );
    unique(
      'widgets',
      manifest.widgets?.map((widget) => widget.id),
    );
    unique(
      'commands',
      manifest.commands?.map((command) => command.id),
    );
    unique(
      'mcp',
      manifest.mcp?.map((server) => server.id),
    );
    unique(
      'events',
      manifest.events?.map((event) => event.type),
    );
    unique(
      'themes',
      manifest.themes?.map((theme) => theme.id),
    );
    if ((manifest.themes?.length ?? 0) > 0 && manifest.files === undefined)
      ctx.addIssue({
        code: 'custom',
        path: ['themes'],
        message: 'Theme files are shipped in the plugin\'s "files" folder.',
      });
    if ((manifest.events?.length ?? 0) > 0 && !manifest.permissions.includes('events'))
      ctx.addIssue({
        code: 'custom',
        path: ['events'],
        message: 'Publishing events needs the "events" permission.',
      });
    // A type's outputs name the artifact action of the type that renders them.
    for (const [index, type] of (manifest.types ?? []).entries())
      for (const [at, output] of (type.outputs ?? []).entries()) {
        const action = manifest.actions?.find((candidate) => candidate.id === output.action);
        if (
          action === undefined ||
          action.where !== 'artifact' ||
          (action.when?.types !== undefined && !action.when.types.includes(type.type))
        )
          ctx.addIssue({
            code: 'custom',
            path: ['types', index, 'outputs', at, 'action'],
            message: `An output is rendered by an artifact action of ${type.type}.`,
          });
      }
    if (manifest.types?.length) {
      const namespace = manifest.name;
      for (const [index, type] of manifest.types.entries())
        if (!type.type.startsWith(`${namespace}/`))
          ctx.addIssue({
            code: 'custom',
            path: ['types', index, 'type'],
            message: `Types are namespaced by the plugin name: "${namespace}/…"`,
          });
    }
    const declared = new Set<string>(manifest.permissions);
    if ((manifest.commands?.length ?? 0) > 0 && !declared.has('commands'))
      ctx.addIssue({
        code: 'custom',
        path: ['permissions'],
        message: 'Commands need the "commands" permission.',
      });
    for (const [index, command] of (manifest.commands ?? []).entries()) {
      if (command.cwd === undefined) continue;
      const param = command.params?.[command.cwd];
      if (param === undefined || !('artifact' in param) || param.file !== undefined)
        ctx.addIssue({
          code: 'custom',
          path: ['commands', index, 'cwd'],
          message: 'A command starts in a directory artifact parameter.',
        });
    }
    const connectionServices = new Set(
      manifest.connections?.map((connection) => connection.service),
    );
    for (const [index, server] of (manifest.mcp ?? []).entries())
      if (server.connection !== undefined && !connectionServices.has(server.connection))
        ctx.addIssue({
          code: 'custom',
          path: ['mcp', index, 'connection'],
          message: 'Undeclared connection',
        });
    for (const [index, connection] of (manifest.connections ?? []).entries())
      if (
        (connection.auth !== undefined || connection.test !== undefined) &&
        !connection.service.startsWith(`${manifest.name}/`)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['connections', index, 'auth'],
          message: `Only its own apps (${manifest.name}/<app>) get a type from a plugin.`,
        });
    if (
      manifest.page !== undefined &&
      'url' in manifest.page &&
      typeof manifest.page.url === 'object' &&
      !connectionServices.has(manifest.page.url.connection.service)
    )
      ctx.addIssue({ code: 'custom', path: ['page', 'url'], message: 'Undeclared connection' });
    for (const [index, provided] of (manifest.provides ?? []).entries())
      if (provided.connection !== undefined && !connectionServices.has(provided.connection))
        ctx.addIssue({
          code: 'custom',
          path: ['provides', index, 'connection'],
          message: 'Undeclared connection',
        });
  });
export type CrowdexManifest = z.infer<typeof crowdexManifestSchema>;

/** The origins an app may reach with `fetch`, from its `network:` permissions. */
export function networkOrigins(manifest: Pick<CrowdexManifest, 'permissions'>): string[] {
  return manifest.permissions.flatMap((permission) =>
    permission.startsWith('network:') ? [permission.slice('network:'.length)] : [],
  );
}

/** Whether `manifest` holds `capability` (a capability name or `agents.unattended`, …). */
export function hasPermission(
  manifest: Pick<CrowdexManifest, 'permissions'>,
  permission: CrowdexPermission,
): boolean {
  return manifest.permissions.includes(permission);
}

/**
 * What installing a plugin grants, as the install prompt lists it: permissions,
 * connections, owned artifact types, agents, commands (their argv) and MCP servers.
 */
export function manifestGrants(manifest: CrowdexManifest): string[] {
  return [
    ...manifest.permissions.map((permission) => `permission:${permission}`),
    ...(manifest.connections ?? []).map((connection) => `connection:${connection.service}`),
    ...(manifest.types ?? []).map((type) => `type:${type.type}:${type.match}`),
    ...(manifest.agents ?? []).map((agent) => `agent:${JSON.stringify(agent)}`),
    ...(manifest.commands ?? []).map((command) => `command:${JSON.stringify(command.run)}`),
    ...(manifest.mcp ?? []).map((server) => `mcp:${server.url}`),
  ].filter((grant, index, all) => all.indexOf(grant) === index);
}

/**
 * What an update adds to what the user approved. An empty list activates the update silently;
 * anything else asks again.
 */
export function widenedGrants(approved: readonly string[], next: CrowdexManifest): string[] {
  const before = new Set(approved);
  return manifestGrants(next).filter((grant) => !before.has(grant));
}

/** What the connection hub reads of an app: the connection types it declares and the apps it uses. */
export function pluginConnectionDeclarer(manifest: CrowdexManifest): ConnectionDeclarer {
  const connections = manifest.connections ?? [];
  return {
    name: manifest.name,
    enabled: true,
    ...(manifest.service === undefined ? {} : { crowlingoService: manifest.service }),
    connectors: connections.map((connection) => ({
      service: connection.service,
      ...(connection.title === undefined ? {} : { title: connection.title }),
      ...(connection.description === undefined ? {} : { description: connection.description }),
      ...(connection.auth === undefined ? {} : { auth: connection.auth }),
      ...(connection.test === undefined ? {} : { test: connection.test }),
    })),
    requires: {
      connections: connections.map((connection) => ({
        service: connection.service,
        ...(connection.scopes === undefined ? {} : { scopes: connection.scopes }),
      })),
    },
  };
}
